// src/prf/prfRenewals.js
// The PRF renewal roster, as pure builders over rows the caller has already
// fetched. No React, no network — so the Coverage Advisor's Renewals list, File
// Management's pipeline board and a checker all get the same answers from the
// same code. `RenewalRoster` and `PipelineBoard` are two screens asking one
// question, and this repo's own record is what happens when that question gets
// two implementations: three news handlers drifted twice, two guarantee engines
// disagreed about one farm's forward-sell ceiling.
//
// ═══════════════════════════════════════════════════════════════════
// THE ROSTER IS THE BOOK BEING RENEWED, NOT THE BOOK ALREADY WRITTEN
// ═══════════════════════════════════════════════════════════════════
//
// The crop rosters (corn/soybeans/wheat) list farmers who carry the crop IN the
// renewal year, and that works because those years exist when the list is read:
// CY2027 wheat is sold in autumn 2026 and the policies land as they sell.
//
// PRF cannot be read that way. Measured 2026-08-27 on the live book: CY2026
// holds 42 PRF policies and **CY2027 holds ZERO** — PRF is not sellable until
// the autumn sales closing, so for most of the year the renewal year is empty.
// A roster keyed on it would render an empty page, which reads as "this agency
// writes no PRF" rather than "here are the 42 customers you must call".
//
// ⚠ RE-MEASURED 2026-10-01, FIVE WEEKS LATER AND INSIDE RENEWAL SEASON, AND THE
// SECOND READING MAKES THE CASE STRONGER RATHER THAN DATING IT:
//
//     CY2025   32 policies   32 farms   10,164 ac   $4,689,612
//     CY2026   42 policies   42 farms   14,524 ac   $7,482,977   <- unchanged to the dollar
//     CY2027    5 policies    5 farms        0 ac           $0   <- shells, no acres keyed
//
// CY2027 is no longer empty — it holds five policy shells with NO acres and NO
// liability, the "acreage not yet keyed" shape this repo documents elsewhere. So a
// roster keyed on the renewal year alone would now be WORSE than empty: it would
// show five farms at zero acres and zero dollars, which reads as a book that lost
// 88% of its PRF rather than as a season that has barely started. Unioning the base
// year is what keeps the other 42 on the call list, and `renewed` being a COLUMN is
// what stops those five shells counting as finished work.
//
// So rows come from the BASE year (renewal year − 1) UNIONED with the renewal
// year, and "have they renewed" is a COLUMN rather than the filter. That is the
// coverage-first inversion `RenewalRoster` already states in its own header —
// the customer who most needs a call is the one with no paperwork yet, and a
// document-first list is exactly the list that hides them.
//
// ⚠ NO SALES CLOSING DATE IS PRINTED OR ASSUMED ANYWHERE HERE. RMA publishes
// PRF's sales closing per COUNTY in the actuarial documents, and this database
// holds no such table — every column in the public schema was scanned on
// 2026-08-27 and the only sales/closing columns belong to DRP and LRP. Kevin's
// standing call covers exactly this ("if you cant automate RMA deadlines, then
// leave it out"), because a hardcoded deadline is a MISSED deadline the day it
// drifts. The renewal year is therefore derived from EVIDENCE — the book and
// the folders — never from a month somebody remembered.

// ── the two documents, and why there are only two ────────────────────
// PRF runs ONE renewal document through the stage folders, and the SOI comes
// back at the end. The filenames mix "PRF Application", "PRF Acreage Reporting
// form", "PRF Renewal AR Packet" and "PRF App and AR form" — often combined in
// one PDF — while the stage folders apply to the FOLDER, not to a doc kind, so
// splitting them into two columns by filename would invent a distinction the
// workflow does not make. See DOC_LADDER in api/_dropbox.js.
const PRF_RENEWAL_STEPS = [
  { key: "prf", label: "Renewal / AR", doc: "prf" },
  { key: "soi", label: "SOI", doc: "prf_soi" },
];

// Mirrors RENEWAL_RANK / STAGE_RANK. Kept as its own constant rather than
// imported from App.jsx (which exports nothing) — the checker asserts the two
// agree, so a change in one that is not made in the other fails rather than
// silently ranking a PRF farmer differently from a wheat one.
const PRF_STAGE_RANK = {
  blank: 0, need_signed: 1, pre_prints: 1, signed_pending: 2, signed_ready: 3,
  ready_for_silas: 4, silas_filled: 5, no_changes: 6, keyed: 6,
};
const PRF_STAGE_DONE = new Set(["keyed", "no_changes"]);

// The longest 6+ digit run in a policy number — the same rule the Pipeline
// board and the Renewal roster already use to key a Dropbox filename to a
// policy. NOT re-normalised here: `allCoresWide` in api/_dropbox.js already
// indexes every stage under BOTH the zero-padded and zero-stripped spelling of
// each core, so a lookup with either form lands. Measured 2026-08-27 — 13 of
// the 32 files in the 2025 PRF SOI folder match only after the leading zero is
// dropped, and that widening is already done on the scan side.
function prfPolicyCore(s) {
  const runs = String(s || "").match(/\d{6,}/g);
  return runs && runs.length ? runs.slice().sort((a, b) => b.length - a.length)[0] : null;
}

// ── which crop year is being renewed ────────────────────────────────
// Derived from the BOOK: the newest crop year holding a PRF policy, plus one.
// Today that is 2026 + 1 = 2027, which is the season being sold this autumn.
//
// It rolls on its own the moment the first policy of a new year lands, and it
// needs no date to do it. A book with no PRF at all falls back to the current
// year so the page renders a named empty state rather than NaN.
//
// The Dropbox folder is deliberately NOT consulted. A caller scans exactly one
// year's PRF root at a time, so it could only ever confirm the year it was
// already given — and a parameter no producer can supply is the dead-guard
// shape this repo has already paid for once (`associatedIifKey`, which read
// `undefined` for the life of the feature while its unit test passed).
//
// A derived default is never a cage: the year selector overrides it.
// ⚠ COUNT ONLY YEARS SOMEBODY ACTUALLY ELECTED. A CARRIER-OPENED SHELL IS NOT A
// BOOK YEAR. PRF is continuous — it carries over unless the grower cancels — so the
// AIP opens next year's policy record on its own, long before anything is sold on
// it. Taking `max(every policy row's crop year) + 1` therefore counted those shells
// and ran the whole roster a year fast.
//
// Measured on the live book 2026-10-06: five CY2027 PRF rows exist and carry NO
// coverage level, NO acres, NO liability, NO premium and ZERO intervals, while all
// 42 CY2026 rows carry every one of them. The old rule saw max=2027 and defaulted
// the page to **CY2028** — two crop years out, which is what Kevin reported.
// Counting elected years only, the newest real book year is 2026 and the renewal is
// 2027.
//
// `prfRenewalIsElected` is the SAME rule the roster uses to decide whether a farm
// has renewed; a second definition of "is this real" is how the headline count and
// the year picker come to disagree about the same policy.
//
// ⚠ AND IT STILL DOES NOT MOVE WITH THE CALENDAR, which was a deliberate choice
// with its own assertions: PRF sales closing is per county in RMA's actuarial
// documents and this database holds no such table, so a date-driven default would
// be inventing the one fact the page already says it does not have.
function prfRenewalYear({ prfPolicies = [], now = new Date() } = {}) {
  const years = (prfPolicies || [])
    .filter((p) => p && prfRenewalIsElected({
      acres: p.totalAcres, liability: p.totalLiability,
      premium: p.totalPremium, intervals: (p.intervals || []).length,
    }))
    .map((p) => Number(p.cropYear))
    .filter((y) => Number.isFinite(y) && y > 1900);
  return years.length ? Math.max(...years) + 1 : now.getFullYear();
}

// ── the roster ──────────────────────────────────────────────────────
// `prfPolicies` are rows shaped like the `prfPolicies` the farmer loader
// builds: { cropYear, policyNumber, county, coverageLevel, totalAcres,
// totalLiability, totalPremium, state, aip, intervals[] }, each carrying the
// `farmerId` of its owner. `stages` is the `docs` object the Dropbox PRF scan
// returns: { prf: {stages:{core:stage}}, prf_soi: {stages:{core:…}} }.
//
// `agentOf(policyNumber)` is supplied by the caller rather than re-derived
// here. Whose policy it is has ONE rule in this app — `policyAgentOf`, which
// carries a manual AOR across crop years by policy stem — and a second copy
// here would be a copy that drifts. A caller with no agent information passes
// nothing and every row reports its agents as unknown.
function buildPrfRenewalRows({
  prfPolicies = [],
  namesById = {},
  year,                 // the renewal year
  stages = null,        // the Dropbox PRF scan's `docs`, or null when it failed
  agentOf = null,
} = {}) {
  const renewalYear = Number(year);
  const baseYear = renewalYear - 1;

  const byFarmer = new Map();
  const touch = (fid) => {
    if (!byFarmer.has(fid)) {
      byFarmer.set(fid, {
        fid, name: namesById[fid] || "—",
        base: null, renewal: null,
        cores: new Set(), counties: new Set(), states: new Set(),
        agents: new Set(), grids: new Set(), coverageLevels: new Set(),
        aips: new Set(),
      });
    }
    return byFarmer.get(fid);
  };

  // Every PRF core the farmer has EVER held, across all crop years and both
  // carriers. Scoping the cores to one year would break the match outright:
  // measured 2026-08-27, six of the twelve farmers holding PRF in both 2025 and
  // 2026 CHANGED CARRIER between them — NAU "NY-944-6088173-25" became R&H
  // "MP-0931313", a different number with a different core — so a renewal
  // document named with either number must still find its customer.
  for (const p of prfPolicies) {
    if (!p || !p.farmerId) continue;
    const g = touch(p.farmerId);
    const core = prfPolicyCore(p.policyNumber);
    if (core) g.cores.add(core);
    if (p.aip) g.aips.add(p.aip);

    const cy = Number(p.cropYear);
    const slot = cy === renewalYear ? "renewal" : cy === baseYear ? "base" : null;
    if (!slot) continue;                 // an older PRF year is history, not this renewal

    const acc = g[slot] || (g[slot] = {
      policies: [], policyIds: [], acres: null, liability: null, premium: null, intervals: 0,
    });
    acc.policies.push(p.policyNumber || null);
    // ⚠ ONE ENTRY PER POLICY, DE-DUPED, AND PAIRED WITH ITS NUMBER. A PRF policy
    // carries several `prf_policies` rows (one per county), so the same parent id
    // arrives repeatedly — and the RH-5237 names a SINGLE policy in item 4, so the
    // button has to offer one sheet per policy rather than per county row. Pushing
    // blind would offer the same sheet four times.
    if (p.policyId && !acc.policyIds.some((x) => x.id === p.policyId)) {
      acc.policyIds.push({ id: p.policyId, number: p.policyNumber || null });
    }
    // NULL is not zero. A PRF policy whose acres the AIP has not keyed yet must
    // read blank, not 0.00 — the same rule the crop rosters follow for a fall
    // crop before its acreage reporting date.
    if (p.totalAcres != null) acc.acres = (acc.acres || 0) + Number(p.totalAcres);
    if (p.totalLiability != null) acc.liability = (acc.liability || 0) + Number(p.totalLiability);
    if (p.totalPremium != null) acc.premium = (acc.premium || 0) + Number(p.totalPremium);
    acc.intervals += (p.intervals || []).length;

    // Facets come from the CURRENT structure — the base year's policy, or the
    // renewal itself once written. A county the farmer insured two years ago is
    // not what an agent filters on today.
    if (p.state) g.states.add(String(p.state).toUpperCase());
    if (p.county) g.counties.add(String(p.county).toUpperCase());
    if (p.coverageLevel != null) g.coverageLevels.add(Number(p.coverageLevel));
    for (const iv of p.intervals || []) {
      if (iv && iv.gridId != null) g.grids.add(String(iv.gridId));
      if (iv && iv.county) g.counties.add(String(iv.county).toUpperCase());
    }
    if (agentOf) { const a = agentOf(p.policyNumber, p); if (a) g.agents.add(a); }
  }

  // Most-advanced-wins across every core the farmer holds, exactly as the crop
  // roster does. An unrecognised stage is IGNORED rather than ranked — a folder
  // spelling we have not seen must never be silently given a position on the
  // ladder. `null` therefore means "the scan found nothing for this customer",
  // which is a different answer from `blank` ("a file is sitting loose in the
  // root, unfiled").
  const stageFor = (cores, docKey) => {
    const map = stages && stages[docKey] && stages[docKey].stages;
    if (!map) return null;
    let best = null;
    for (const c of cores) {
      const st = map[c];
      if (!st || !(st in PRF_STAGE_RANK)) continue;
      if (best === null || PRF_STAGE_RANK[st] > PRF_STAGE_RANK[best]) best = st;
    }
    return best;
  };

  const rows = [...byFarmer.values()]
    .filter((g) => g.base || g.renewal)     // an older-only PRF farmer is not on this renewal
    .map((g) => {
      const cores = [...g.cores];
      const docStage = stageFor(cores, "prf");
      // The SOI folder is flat, so every core in it lands on `blank`. What
      // matters is PRESENCE, not the stage word — an SOI is either on file or
      // it is not, and rendering "in folder · unsorted" beside it would be
      // describing our filing rather than the customer's document.
      const soiOnFile = stageFor(cores, "prf_soi") != null;
      return {
        fid: g.fid,
        name: g.name,
        cores,
        base: g.base,
        renewal: g.renewal,
        // AIP-SOURCED, never inferred: the renewal exists when the AIP's own
        // file names a PRF policy for that crop year. Nothing about a document
        // makes a policy real.
        //
        // ⚠ BUT A POLICY RECORD EXISTING IS NOT A RENEWAL BEING WRITTEN, and for
        // PRF that gap is the normal case rather than an edge one. PRF is
        // CONTINUOUS — it carries over unless the grower cancels — so NAU opens
        // next year's policy record automatically, long before sales closing
        // (Dec 1 for the following crop year). The record is real; the grower has
        // elected nothing on it.
        //
        // MEASURED 2026-10-01, and the separation is total: all 5 CY2027 PRF rows
        // carry NO coverage level, NO productivity factor, NO acres, NO liability,
        // NO premium and ZERO intervals, while 32 of 32 CY2025 rows and 42 of 42
        // CY2026 rows carry every one of them. All five are NAU; R&H opens no
        // CY2027 shells at all, which is why the page showed "written" against
        // NAU names only.
        //
        // So `renewed` means ELECTED, and a shell is its OWN answer — neither
        // written nor nothing-filed. Reporting a shell as written puts a farm on
        // the done pile that nobody has spoken to; dropping it would hide that the
        // carrier has already opened the record, which is a real thing to know
        // when you ring them. This is `acreage-not-yet-keyed` on a second surface.
        renewed: prfRenewalIsElected(g.renewal),
        renewalShell: !!g.renewal && !prfRenewalIsElected(g.renewal),
        docStage,
        docDone: PRF_STAGE_DONE.has(docStage),
        soiOnFile,
        counties: [...g.counties].sort(),
        states: [...g.states].sort(),
        grids: [...g.grids].sort(),
        coverageLevels: [...g.coverageLevels].sort((a, b) => a - b),
        aips: [...g.aips].sort(),
        agents: [...g.agents],
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  return { rows, renewalYear, baseYear };
}

// ── documents in the folder that belong to nobody we know ───────────
// A packet whose core matches no customer is the one failure this roster cannot
// see from the row side: the farmer reads "nothing prepared" while their signed
// document sits in the folder. It happens for real — a farmer written under a
// NEW policy number we have not synced yet, or a customer whose PRF we do not
// hold. Measured 2026-08-27, the 2025 PRF KEYED folder carries two such files.
//
// So it is COUNTED AND NAMED rather than dropped, which is the same convention
// the news filter, the MI map and the grain guarantee all follow: a quiet
// omission reads as "there is nothing there".
function prfUnmatchedDocCores({ stages = null, rows = [], docKey = "prf" } = {}) {
  const map = stages && stages[docKey] && stages[docKey].stages;
  if (!map) return [];
  const known = new Set();
  for (const r of rows) for (const c of r.cores || []) known.add(c);
  return Object.keys(map).filter((c) => !known.has(c)).sort();
}

// HAS THIS RENEWAL ACTUALLY BEEN ELECTED, or has the carrier merely opened the
// record? PRF is continuous, so a policy row for next year appears on its own.
//
// ⚠ ANY ONE ELECTION COUNTS, NOT ALL OF THEM. A renewal keyed in stages — intervals
// entered before the premium is computed, say — is genuinely under way, and
// requiring every field would read a half-keyed renewal as untouched. The shell is
// the case where the AIP has stated NOTHING, which is what was measured.
//
// ⚠ NULL IS NOT ZERO AND ZERO IS NOT AN ELECTION. `acres` et al. accumulate only
// from non-null values (see the accumulator above), so null means "not keyed". A
// stored 0 is not an election either — nobody insures zero acres — so both read as
// a shell, and only a positive figure or an interval row counts.
function prfRenewalIsElected(slot) {
  if (!slot) return false;
  if ((slot.intervals || 0) > 0) return true;
  return [slot.acres, slot.liability, slot.premium].some((v) => v != null && Number(v) > 0);
}

// Headline counts. `renewed` and `docDone` are deliberately separate: a policy
// can be written before its paperwork is keyed and vice versa, and collapsing
// them into one "complete" would hide whichever half is outstanding.
function prfRenewalTotals(rows = []) {
  const t = {
    farmers: rows.length, renewed: 0, notRenewed: 0, shells: 0,
    docStarted: 0, docDone: 0, nothingFiled: 0, soiOnFile: 0,
    baseAcres: null, baseLiability: null,
  };
  for (const r of rows) {
    if (r.renewed) t.renewed++; else t.notRenewed++;
    // Counted SEPARATELY and not folded into either side: a shell is still work
    // outstanding (so it is not `renewed`), but it is not the same call as a farm
    // the carrier has no record for at all.
    if (r.renewalShell) t.shells++;
    if (r.docStage) t.docStarted++;
    if (r.docDone) t.docDone++;
    if (!r.docStage && !r.renewed) t.nothingFiled++;
    if (r.soiOnFile) t.soiOnFile++;
    const b = r.base;
    if (b && b.acres != null) t.baseAcres = (t.baseAcres || 0) + b.acres;
    if (b && b.liability != null) t.baseLiability = (t.baseLiability || 0) + b.liability;
  }
  return t;
}

module.exports = {
  PRF_RENEWAL_STEPS,
  PRF_STAGE_RANK,
  PRF_STAGE_DONE,
  prfPolicyCore,
  prfRenewalYear,
  buildPrfRenewalRows,
  prfUnmatchedDocCores,
  prfRenewalTotals,
  prfRenewalIsElected,
};
